/**
 * The `/analytics` Events table: which GA4 event rows it shows, and in what
 * order.
 *
 * Key events come first, then the rest by count. The rows that matter most
 * are the rarest: three purchases in 28 days sit below thousands of page
 * views, scrolls and clicks. Until 2026-10-11 the table showed the 12 most
 * frequent event names, and a sale was never one of them. Collapsed, the
 * table is still 12 rows, but it never cuts a key event.
 *
 * A snapshot stored before that date has no `key_events`, so nothing in it
 * reads as a key event and its rows simply keep their count order.
 */

export interface Ga4EventRow {
  readonly event: string;
  readonly count: number | null;
  /** GA4 counted at least one of this name's events as a key event. */
  readonly keyEvent: boolean;
}

/** Rows the collapsed table shows; more when there are more key events than that. */
export const GA4_EVENTS_COLLAPSED = 12;

/** Key events first, then the rest; each group by count, largest first. */
export function orderGa4Events(
  rows: ReadonlyArray<Ga4EventRow>,
): Ga4EventRow[] {
  return [...rows].sort(
    (a, b) =>
      Number(b.keyEvent) - Number(a.keyEvent) ||
      (b.count ?? 0) - (a.count ?? 0),
  );
}

/** The collapsed view of rows already in {@link orderGa4Events} order. */
export function collapseGa4Events(
  ordered: ReadonlyArray<Ga4EventRow>,
): Ga4EventRow[] {
  const keyEvents = ordered.filter((r) => r.keyEvent).length;
  return ordered.slice(0, Math.max(GA4_EVENTS_COLLAPSED, keyEvents));
}
